<template>
  <div v-show="showReportManager">
    <q-card square flat bordered class="my-card bg-grey-1 vertical-bottom">
      <q-toolbar>
        <q-toolbar-title class="text-weight-bold text-grey-brand"
          >Report Manager</q-toolbar-title
        >
        <q-btn
          flat
          round
          dense
          icon="close"
          @click="onReportManagerCloseClick"
        />
      </q-toolbar>

      <!-- Report type selection. -->
      <q-card-section>
        <div class="column">
          <q-select
            v-model="selectedReportType"
            dense
            color="blue-brand"
            outlined
            :options="reportTypeOptions"
            label="Select Report Type"
            @update:model-value="onReportTypeSelect"
          />
        </div>
      </q-card-section>

      <!-- The completed pig run report query controls. -->
      <q-card-section v-show="showReportTypeCompletedPigRun">
        <div class="column">
          <div v-if="completedPigRuns.length > 0">
            <q-select
              v-model="selectedCompletedPigRun"
              dense
              color="blue-brand"
              outlined
              :options="completedPigRunOptions"
              label="Select Completed Pig Run"
              popup-content-style="white-space: normal; word-break: break-word;"
              @update:model-value="onCompletedPigRunSelect"
            >
              <template #selected-item="scope">
                <span
                  style="
                    white-space: normal;
                    word-break: break-word;
                    line-height: 1.3;
                  "
                >
                  {{ scope.opt.label }}
                </span>
              </template>
            </q-select>
            <PigRunSummary
              ref="pigRunSummary"
              :pig-run-id="selectedCompletedPigRunId"
            />
            <q-checkbox
              v-model="selectedPigRunIncludeVerifiedPassagesOnly"
              color="blue-brand"
              >Include Verified Passages Only</q-checkbox
            >
            <q-btn
              class="btn-brand"
              label="Generate Report Data"
              dense
              no-caps
              :loading="completedPigRunReportGenerateReportLoading"
              @click="onCompletedPigRunGenerateReportClick"
            />
          </div>
          <div v-else>There are no completed pig runs to report on.</div>
        </div>
      </q-card-section>

      <!-- The devices report query controls. -->
      <q-card-section v-show="showReportTypeDevice">
        <div class="column">
          <q-btn
            class="btn-brand"
            label="Enter report parameters"
            dense
            no-caps
            @click="onEnterReportParametersForDevicesReportClick"
          />

          <!-- Parameter summary -->
          <div
            v-if="hasDevicesReportParameters"
            class="q-mt-sm q-pa-sm"
            style="
              background: #f5f5f5;
              border-radius: 4px;
              font-size: 12px;
              line-height: 1.4;
            "
          >
            <div class="text-bold q-mb-xs">Parameters</div>
            <div>
              <span class="text-grey-7">Start:</span>
              {{ startDateDevicesReport }}
            </div>
            <div>
              <span class="text-grey-7">End:</span>
              {{ endDateDevicesReport }}
            </div>
            <div class="q-mt-xs">
              <span class="text-grey-7"
                >AGMs ({{ selectedDevicesForDevicesReport.length }}):</span
              >
              {{ truncatedDeviceLabelsForDevicesReport }}
              <a
                v-if="selectedDevicesForDevicesReport.length > 5"
                href="#"
                style="font-size: 11px"
                @click.prevent="showAllAgmsSummary = !showAllAgmsSummary"
              >
                {{ showAllAgmsSummary ? 'Show less' : 'Show all' }}
              </a>
            </div>
          </div>

          <br />
          <q-btn
            class="btn-brand"
            label="Generate Report Data"
            dense
            no-caps
            :loading="devicesReportGenerateReportLoading"
            @click="onGenerateReportDataForDevicesReportClick"
          />
        </div>
      </q-card-section>
    </q-card>

    <!-- The enter parameters for devices report dialog. -->
    <q-dialog v-model="showEnterParametersForDevicesReportDialog">
      <q-card style="width: 600px; max-width: 90vm">
        <q-toolbar>
          <q-toolbar-title class="text-blue-brand text-bold"
            >Report Parameters</q-toolbar-title
          >
          <q-btn v-close-popup flat round dense icon="close" />
        </q-toolbar>
        <q-card-section>
          <div class="column">
            <div class="col q-pb-sm">
              <!-- Start date/time -->
              <q-input
                v-model="startDateDevicesReport"
                dense
                filled
                label="Start Date (UTC)"
              >
                <template #prepend>
                  <q-icon name="event" class="cursor-pointer">
                    <q-popup-proxy
                      ref="qStartDateProxy"
                      transition-show="scale"
                      transition-hide="scale"
                    >
                      <q-date
                        v-model="startDateDevicesReport"
                        mask="YYYY-MM-DD HH:mm:ss"
                        @update:model-value="hideStartDateProxy"
                      />
                    </q-popup-proxy>
                  </q-icon>
                </template>
                <template #append>
                  <q-icon name="access_time" class="cursor-pointer">
                    <q-popup-proxy
                      ref="qStartTimeProxy"
                      transition-show="scale"
                      transition-hide="scale"
                    >
                      <q-time
                        v-model="startDateDevicesReport"
                        mask="YYYY-MM-DD HH:mm:ss"
                        format24h
                        @update:model-value="hideStartTimeProxy"
                      />
                    </q-popup-proxy>
                  </q-icon>
                </template>
              </q-input>

              <!-- End date/time -->
              <q-input
                v-model="endDateDevicesReport"
                dense
                filled
                label="End Date (UTC)"
              >
                <template #prepend>
                  <q-icon name="event" class="cursor-pointer">
                    <q-popup-proxy
                      ref="qEndDateProxy"
                      transition-show="scale"
                      transition-hide="scale"
                    >
                      <q-date
                        v-model="endDateDevicesReport"
                        mask="YYYY-MM-DD HH:mm:ss"
                        @update:model-value="hideEndDateProxy"
                      />
                    </q-popup-proxy>
                  </q-icon>
                </template>
                <template #append>
                  <q-icon name="access_time" class="cursor-pointer">
                    <q-popup-proxy
                      ref="qEndTimeProxy"
                      transition-show="scale"
                      transition-hide="scale"
                    >
                      <q-time
                        v-model="endDateDevicesReport"
                        mask="YYYY-MM-DD HH:mm:ss"
                        format24h
                        @update:model-value="hideEndTimeProxy"
                      />
                    </q-popup-proxy>
                  </q-icon>
                </template>
              </q-input>
            </div>
          </div>
          <q-separator />

          <div class="column" style="max-height: 300px; overflow-y: auto">
            <div class="col q-pb-sm">
              <q-option-group
                v-model="selectedDevicesForDevicesReport"
                dense
                :options="allDeviceOptionsForDevicesReport"
                color="indigo-9"
                type="checkbox"
              />
            </div>
          </div>

          <q-separator />

          <q-card-actions align="right">
            <q-btn
              class="btn-brand"
              label="Set Parameters"
              type="button"
              no-caps
              @click="onSetParametersForDevicesReportClick"
            />
          </q-card-actions>
        </q-card-section>
      </q-card>
    </q-dialog>

    <!-- The completed pig run report dialog. -->
    <q-dialog v-model="showCompletedPigRunReportDialog">
      <q-card style="width: 1150px; max-width: 90vw">
        <q-toolbar>
          <q-toolbar-title class="text-blue-brand text-bold"
            >Completed Pig Run Report Data</q-toolbar-title
          >
          <q-btn
            flat
            round
            dense
            icon="cloud_download"
            @click="onDownloadCompletedPigRunReportData"
          />
          <q-btn
            flat
            round
            dense
            icon="filter_list"
            @click="onToggleExportOptionsForCompletedPigRunReport"
          />
          <q-btn v-close-popup flat round dense icon="close" />
        </q-toolbar>

        <div
          v-show="exportOptionsVisibleCompletedPigRunReport"
          class="row justify-center items-start q-gutter-md"
        >
          <span
            ><q-checkbox
              v-model="selectedPigRunIncludeVerifiedPassagesOnly"
              color="blue-brand"
              @update:model-value="
                onIncludeVerifiedPassagesOnlyCompletedPigRunReportInput
              "
            />Include verified passages only</span
          >
        </div>

        <q-separator />
        <q-table
          v-model:pagination="completedPigRunReportDataPagination"
          table-header-class="bg-blue-brand text-white"
          :rows="completedPigRunReportData"
          :columns="completedPigRunReportColumns"
          row-key="passageTimeStamp"
          dense
          binary-state-sort
          @request="onCompletedPigRunReportRequest"
        >
          <template #body-cell-passageTimeStamp="props">
            <q-td :props="props">
              {{ props.row.passageTimeStamp }}
            </q-td>
          </template>

          <template #body-cell-markerName="props">
            <q-td :props="props">
              {{ props.row.markerName }}
            </q-td>
          </template>

          <template #body-cell-toolName="props">
            <q-td :props="props">
              {{ props.row.toolName }}
            </q-td>
          </template>

          <template #body-cell-coordinates="props">
            <q-td :props="props">
              {{
                formatCoordinate(props.row.latitude, false) +
                formatCoordinate(props.row.longitude, true) +
                formatAltitude(props.row.altitude, true)
              }}
            </q-td>
          </template>

          <template #body-cell-uid="props">
            <q-td :props="props">
              {{ props.row.uid }}
            </q-td>
          </template>

          <template #body-cell-detection-count="props">
            <q-td :props="props">
              {{ props.row.detectionCount }}
            </q-td>
          </template>

          <template #body-cell-passage-detected-frequency="props">
            <q-td :props="props">
              {{ props.row.detectionFrequency }}
            </q-td>
          </template>

          <template #body-cell-graph-data="props">
            <q-td :props="props">
              <span v-if="isOnlySingleTypeOfPassage(props)">
                <q-btn
                  v-for="waveform in props.row.passageWaveforms"
                  :key="waveform.passageWaveformId"
                  size="sm"
                  class="q-mr-xs btn-brand"
                  dense
                  icon="show_chart"
                  :label="getWaveformLabel(waveform.passageWaveformType)"
                  @click="
                    showChart(
                      props,
                      getWaveformLabel(waveform.passageWaveformType)
                    )
                  "
                ></q-btn>
              </span>
              <span v-else>
                <q-btn-dropdown
                  size="sm"
                  class="q-mr-xs btn-brand"
                  dense
                  icon="show_chart"
                >
                  <q-list dense>
                    <q-item
                      v-for="waveform in props.row.passageWaveforms"
                      :key="waveform.passageWaveformId"
                      v-close-popup
                      clickable
                      @click="
                        showChart(
                          { row: props.row },
                          getWaveformLabel(waveform.passageWaveformType)
                        )
                      "
                    >
                      <q-item-section>
                        <q-item-label>{{
                          getWaveformLabel(waveform.passageWaveformType)
                        }}</q-item-label>
                      </q-item-section>
                    </q-item>
                  </q-list>
                </q-btn-dropdown>
              </span>
            </q-td>
          </template>

          <template #body-cell-verify-passage="props">
            <q-td :props="props">
              <q-btn
                size="sm"
                class="btn-brand"
                dense
                :color="props.row.isVerified ? 'green' : 'red'"
                :icon="props.row.isVerified ? 'check_circle_outline' : 'block'"
                @click="onVerifyPassageForCompletedPigRun(props.row)"
              />
            </q-td>
          </template>

          <template #body-cell-machine-learning-data="props">
            <q-td :props="props">
              <template v-for="type in ['elf', 'geo', 'mag', 'acc', 'tmr']">
                <MLBadge
                  v-if="
                    props.row[`${type}MachineLearningResultList`] !==
                      undefined &&
                    props.row[`${type}MachineLearningResultList`].length !== 0
                  "
                  :key="type"
                  :type="type"
                  :results="props.row[`${type}MachineLearningResultList`]"
                />
              </template>
            </q-td>
          </template>
        </q-table>
      </q-card>
    </q-dialog>

    <!-- Validation message dialog. -->
    <q-dialog v-model="showValidationMessageDialog">
      <q-card>
        <q-card-section>
          <div class="text-h6">{{ validationDialogTitle }}</div>
        </q-card-section>

        <q-card-section class="q-pt-none">{{
          validationDialogMessage
        }}</q-card-section>

        <q-card-actions align="right">
          <q-btn v-close-popup flat label="OK" color="primary" />
        </q-card-actions>
      </q-card>
    </q-dialog>

    <!-- The devices report dialog. -->
    <q-dialog v-model="showDevicesReportDialog">
      <q-card style="width: 1150px; max-width: 90vw">
        <q-toolbar>
          <q-toolbar-title class="text-blue-brand text-bold"
            >By Agm(s) Report Data</q-toolbar-title
          >
          <q-btn
            flat
            round
            dense
            icon="cloud_download"
            @click="onDownloadDevicesReportData"
          />
          <q-btn
            flat
            round
            dense
            icon="filter_list"
            @click="onToggleExportOptionsForDevicesReport"
          />
          <q-btn v-close-popup flat round dense icon="close" />
        </q-toolbar>

        <div
          v-show="exportOptionsVisibleDevicesReport"
          class="row justify-center items-start q-gutter-md"
        >
          <span
            ><q-checkbox
              v-model="includeVerifiedPassagesOnlyDevicesReport"
              color="blue-brand"
              @update:model-value="
                onIncludeVerifiedPassagesOnlyDevicesReportInput
              "
            />
            Include verified passages only</span
          >
        </div>

        <q-separator />
        <q-table
          v-model:pagination="devicesReportDataPagination"
          table-header-class="bg-blue-brand text-white"
          :rows="devicesReportData"
          :columns="devicesReportColumns"
          row-key="passageTimeStamp"
          dense
          binary-state-sort
          @request="onDevicesReportRequest"
        >
          <template #body-cell-passageTimeStamp="props">
            <q-td :props="props">
              {{ props.row.passageTimeStamp }}
            </q-td>
          </template>

          <template #body-cell-pigRunName="props">
            <q-td :props="props">
              {{ props.row.pigRunName }}
            </q-td>
          </template>

          <template #body-cell-markerName="props">
            <q-td :props="props">
              {{ props.row.markerName }}
            </q-td>
          </template>

          <template #body-cell-toolName="props">
            <q-td :props="props">
              {{ props.row.toolName }}
            </q-td>
          </template>

          <template #body-cell-coordinates="props">
            <q-td :props="props">
              {{
                formatCoordinate(props.row.latitude, false) +
                formatCoordinate(props.row.longitude, true) +
                formatAltitude(props.row.altitude, true)
              }}
            </q-td>
          </template>

          <template #body-cell-uid="props">
            <q-td :props="props">
              {{ props.row.uid }}
            </q-td>
          </template>

          <template #body-cell-detection-count="props">
            <q-td :props="props">
              {{ props.row.detectionCount }}
            </q-td>
          </template>

          <template #body-cell-passage-detected-frequency="props">
            <q-td :props="props">
              {{ props.row.detectionFrequency }}
            </q-td>
          </template>

          <template #body-cell-graph-data="props">
            <q-td :props="props">
              <span v-if="isOnlySingleTypeOfPassage(props)">
                <q-btn
                  v-for="waveform in props.row.passageWaveforms"
                  :key="waveform.passageWaveformId"
                  size="sm"
                  class="q-mr-xs btn-brand"
                  dense
                  icon="show_chart"
                  :label="getWaveformLabel(waveform.passageWaveformType)"
                  @click="
                    showChart(
                      props,
                      getWaveformLabel(waveform.passageWaveformType)
                    )
                  "
                ></q-btn>
              </span>
              <span v-else>
                <q-btn-dropdown
                  size="sm"
                  class="q-mr-xs btn-brand"
                  dense
                  icon="show_chart"
                >
                  <q-list dense>
                    <q-item
                      v-for="waveform in props.row.passageWaveforms"
                      :key="waveform.passageWaveformId"
                      v-close-popup
                      clickable
                      @click="
                        showChart(
                          { row: props.row },
                          getWaveformLabel(waveform.passageWaveformType)
                        )
                      "
                    >
                      <q-item-section>
                        <q-item-label>{{
                          getWaveformLabel(waveform.passageWaveformType)
                        }}</q-item-label>
                      </q-item-section>
                    </q-item>
                  </q-list>
                </q-btn-dropdown>
              </span>
            </q-td>
          </template>

          <template #body-cell-verify-passage="props">
            <q-td :props="props">
              <q-btn
                size="sm"
                class="btn-brand"
                dense
                :color="props.row.isVerified ? 'green' : 'red'"
                :icon="props.row.isVerified ? 'check_circle_outline' : 'block'"
                @click="onVerifyPassageForDevicesReport(props.row)"
              />
            </q-td>
          </template>

          <template #body-cell-machine-learning-data="props">
            <q-td :props="props">
              <template v-for="type in ['elf', 'geo', 'mag', 'acc', 'tmr']">
                <MLBadge
                  v-if="
                    props.row[`${type}MachineLearningResultList`] !==
                      undefined &&
                    props.row[`${type}MachineLearningResultList`].length !== 0
                  "
                  :key="type"
                  :type="type"
                  :results="props.row[`${type}MachineLearningResultList`]"
                />
              </template>
            </q-td>
          </template>
        </q-table>
      </q-card>
    </q-dialog>

    <!-- Waveform selection dialog for verification -->
    <q-dialog v-model="showWaveformSelectionDialog">
      <q-card style="min-width: 300px">
        <q-card-section>
          <div class="text-h6">Select Waveform to Verify</div>
          <div class="text-body2 q-mt-sm">
            Choose which waveform type to verify for this passage.
          </div>
        </q-card-section>
        <q-card-section>
          <q-option-group
            v-model="selectedWaveformIdForVerify"
            :options="waveformOptionsForVerify"
            type="radio"
          />
        </q-card-section>
        <q-card-actions align="right">
          <q-btn
            flat
            label="Cancel"
            class="btn-brand"
            @click="onCancelWaveformSelection"
          />
          <q-btn
            flat
            label="Confirm"
            class="btn-brand"
            color="primary"
            :disable="selectedWaveformIdForVerify === null"
            @click="onConfirmWaveformSelection"
          />
        </q-card-actions>
      </q-card>
    </q-dialog>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { hidePopupProxy } from '@/utils/QuasarProxyHelper';
import * as dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import PigRunService from '@/services/PigRunService';
import DateCommon from '@/utils/DateCommon';
import { EventBus } from '@/utils/EventBus';
import DisplayMLResult, {
  MachineLearningResultColors,
} from '@/utils/DisplayMLResult';
import { Device } from '@/models/Device';
import { ReportDataForCompletedPigRunRequest } from '@/models/ReportManager/ReportDataForCompletedPigRunRequest';
import { ReportDataForDevicesRequest } from '@/models/ReportManager/ReportDataForDevicesRequest';
import { PassageVerificationRequest } from '@/models/ReportManager/PassageVerificationRequest';
import { DeleteManualPassageRequest } from '@/models/ReportManager/DeleteManualPassageRequest';
import {
  getAvailableWaveforms,
  getVerifiedWaveforms,
  WaveformOption,
} from '@/utils/WaveformUtils';
import { IPassageVerified } from '@/models/EventBus/PassageVerified';
import { ActivePigRun } from '@/models/ActivePigRun';
import PigRunSummary from '@/components/PigRunSummary.vue';
import MLBadge from '@/components/MLBadge.vue';

dayjs.extend(utc);

export default defineComponent({
  name: 'ReportManager',
  components: {
    PigRunSummary,
    MLBadge,
  },
  data() {
    return {
      // Report type
      selectedReportType: '',
      showReportTypeCompletedPigRun: false,
      showReportTypeDevice: false,
      // Completed pig run report
      selectedCompletedPigRun: '',
      selectedPigRunIncludeVerifiedPassagesOnly: false,
      completedPigRunReportGenerateReportLoading: false,
      showCompletedPigRunReportDialog: false,
      completedPigRunReportDataArray: [] as any[],
      completedPigRunReportDataPagination: {} as any,
      exportOptionsVisibleCompletedPigRunReport: false,
      // Devices report
      showEnterParametersForDevicesReportDialog: false,
      startDateDevicesReport: '',
      startDateDevicesReportAsDate: undefined as Date | undefined,
      endDateDevicesReport: '',
      endDateDevicesReportAsDate: undefined as Date | undefined,
      selectedDevicesForDevicesReport: [] as any[],
      devicesReportGenerateReportLoading: false,
      showDevicesReportDialog: false,
      devicesReportDataArray: [] as any[],
      devicesReportDataPagination: {} as any,
      exportOptionsVisibleDevicesReport: false,
      includeVerifiedPassagesOnlyDevicesReport: false,
      showAllAgmsSummary: false,
      // Validation
      showValidationMessageDialog: false,
      validationDialogTitle: '',
      validationDialogMessage: '',
      // Waveform selection dialog
      showWaveformSelectionDialog: false,
      selectedWaveformIdForVerify: null as number | null,
      waveformOptionsForVerify: [] as WaveformOption[],
      pendingVerifyPropsRow: null as any,
      pendingVerifySource: '' as 'devices' | 'completedPigRun' | '',
      // Pig runs list (fetched locally — not from store)
      pigRunsList: [] as ActivePigRun[],
      isPigRunsListLoading: false,
    };
  },
  computed: {
    showReportManager(): boolean {
      return this.$store.state.showReportManager;
    },
    reportTypeOptions(): any[] {
      return [
        { label: 'By Completed Pig Run', value: 'CompletedPigRun' },
        { label: 'By Agm(s)', value: 'Device' },
      ];
    },
    pigRunsListData(): ActivePigRun[] {
      return this.pigRunsList;
    },
    completedPigRuns(): any[] {
      return this.pigRunsListData.filter(
        (pr: any) => pr.lifecycleState === 'D'
      );
    },
    completedPigRunOptions(): any[] {
      return this.completedPigRuns.map((pr: any) => {
        let formattedDate = 'N/A';
        if (pr.receiveDate) {
          const raw = String(pr.receiveDate).trim().replace(' ', 'T');
          const utcStr = raw.endsWith('Z') ? raw : raw + 'Z';
          formattedDate = dayjs
            .default(utcStr)
            .utc()
            .format('YYYY-MM-DD HH:mm');
        }
        return {
          value: pr.pigRunId,
          runData: pr,
          label: formattedDate + ' / ' + pr.name,
        };
      });
    },
    devicesReportData(): any[] {
      return this.devicesReportDataArray;
    },
    completedPigRunReportData(): any[] {
      return this.completedPigRunReportDataArray;
    },
    devicesReportColumns(): any[] {
      return [
        {
          align: 'left',
          name: 'passageTimeStamp',
          label: 'Passage',
          field: 'passageTimeStamp',
          sortable: true,
          required: true,
        },
        {
          align: 'left',
          name: 'pigRunName',
          label: 'Pig Run',
          sortable: false,
        },
        {
          align: 'left',
          name: 'markerName',
          label: 'Marker Name',
          sortable: true,
        },
        {
          align: 'left',
          name: 'toolName',
          label: 'Tool Name',
          sortable: false,
        },
        {
          align: 'left',
          name: 'coordinates',
          label: 'Coordinates - Lat, Lon, Altitude (m)',
          sortable: false,
        },
        {
          align: 'left',
          name: 'UId',
          label: 'UID',
          field: 'uid',
          sortable: true,
        },
        {
          align: 'left',
          name: 'detection-count',
          label: 'Count',
          sortable: false,
        },
        {
          align: 'left',
          name: 'passage-detected-frequency',
          label: 'Frequency',
          field: 'detectionFrequency',
          sortable: false,
        },
        { align: 'left', name: 'graph-data', label: 'Data' },
        { align: 'left', name: 'verify-passage', label: 'Verify' },
        {
          align: 'center',
          field: 'passageId',
          name: 'machine-learning-data',
          label: 'ML Verified',
        },
      ];
    },
    completedPigRunReportColumns(): any[] {
      return [
        {
          align: 'left',
          name: 'passageTimeStamp',
          label: 'Passage',
          field: 'passageTimeStamp',
          sortable: true,
          required: true,
        },
        {
          align: 'left',
          name: 'markerName',
          label: 'Marker Name',
          sortable: true,
        },
        {
          align: 'left',
          name: 'toolName',
          label: 'Tool Name',
          sortable: false,
        },
        {
          align: 'left',
          name: 'coordinates',
          label: 'Coordinates - Lat, Lon, Altitude (m)',
          sortable: false,
        },
        {
          align: 'left',
          name: 'UId',
          label: 'UID',
          field: 'uid',
          sortable: true,
        },
        {
          align: 'left',
          name: 'detection-count',
          label: 'Count',
          sortable: false,
        },
        {
          align: 'left',
          name: 'passage-detected-frequency',
          label: 'Frequency',
          field: 'detectionFrequency',
          sortable: false,
        },
        { align: 'left', name: 'graph-data', label: 'Data' },
        { align: 'left', name: 'verify-passage', label: 'Verify' },
        {
          align: 'center',
          name: 'machine-learning-data',
          label: 'ML Verified',
        },
      ];
    },
    allDeviceOptionsForDevicesReport(): any[] {
      const devices: Device[] = this.$store.state.device.devices || [];
      return devices.map((device) => ({
        label: 'AGM UID ' + device.uid,
        value: device.uid,
      }));
    },
    hasDevicesReportParameters(): boolean {
      return (
        this.startDateDevicesReport !== '' &&
        this.endDateDevicesReport !== '' &&
        this.selectedDevicesForDevicesReport.length > 0
      );
    },
    truncatedDeviceLabelsForDevicesReport(): string {
      const labels = this.selectedDevicesForDevicesReport.map((uid: number) => {
        const opt = this.allDeviceOptionsForDevicesReport.find(
          (o: any) => o.value === uid
        );
        return opt ? opt.label : 'UID ' + uid;
      });
      if (!this.showAllAgmsSummary && labels.length > 5) {
        return labels.slice(0, 5).join(', ') + ' ...';
      }
      return labels.join(', ');
    },
    selectedCompletedPigRunId(): number | undefined {
      const value = (this.selectedCompletedPigRun as any)?.value;
      return typeof value === 'number' ? value : undefined;
    },
  },
  watch: {
    async showReportManager(visible: boolean) {
      if (visible) {
        await this.fetchPigRunsList();
        const preselectedId =
          this.$store.state.reportManagerPreselectedPigRunId;
        if (preselectedId != null) {
          this.selectedReportType = this.reportTypeOptions.find(
            (o: any) => o.value === 'CompletedPigRun'
          );
          this.onReportTypeSelect();
          const match = this.completedPigRunOptions.find(
            (o: any) => o.value === preselectedId
          );
          if (match) {
            this.selectedCompletedPigRun = match;
            this.onCompletedPigRunSelect();
          }
          this.$store.state.reportManagerPreselectedPigRunId = null;
        }
      } else {
        (this.$refs.pigRunSummary as any)?.removeMapPreview();
        this.$store.commit('SET_PLOTTED_PIG_RUN_ID', null);
      }
    },
  },
  async mounted() {
    await this.fetchPigRunsList();
    EventBus.on('passage-verified', this.handlePassageVerified as any);
    EventBus.on(
      'passage-timestamp-updated',
      this.handlePassageTimestampUpdated as any
    );
    EventBus.on('switch-passage', this.handleSwitchPassage as any);
  },

  beforeUnmount() {
    EventBus.off('passage-verified', this.handlePassageVerified as any);
    EventBus.off(
      'passage-timestamp-updated',
      this.handlePassageTimestampUpdated as any
    );
    EventBus.off('switch-passage', this.handleSwitchPassage as any);
  },
  methods: {
    showServiceException(
      logId: any,
      title = 'Report Manager Service Exception'
    ) {
      this.validationDialogMessage =
        'A report manager service exception has occurred. LogId = [' +
        logId +
        ']';
      this.validationDialogTitle = title;
      this.showValidationMessageDialog = true;
    },
    handleException(exception: any, title?: string): boolean {
      if (exception?.response?.data !== undefined) {
        this.showServiceException(exception.response.data, title);
      }
      return false;
    },
    getSelectedReportTypeValue(): string {
      return (this.selectedReportType as any)?.value || '';
    },
    getSelectedCompletedPigRunId(): number | null {
      const value = (this.selectedCompletedPigRun as any)?.value;
      return typeof value === 'number' ? value : null;
    },
    transformReportDataForShowChart(reportDataArray: any[]): any[] {
      return (reportDataArray || []).map((record) => {
        const transformedRecord = { ...record };
        transformedRecord.elfId = undefined;
        transformedRecord.geoId = undefined;
        transformedRecord.magId = undefined;
        transformedRecord.accId = undefined;
        transformedRecord.tmrId = undefined;
        transformedRecord.elfIsVerified = false;
        transformedRecord.geoIsVerified = false;
        transformedRecord.magIsVerified = false;
        transformedRecord.accIsVerified = false;
        transformedRecord.tmrIsVerified = false;
        transformedRecord.elfMachineLearningResultList = [];
        transformedRecord.geoMachineLearningResultList = [];
        transformedRecord.magMachineLearningResultList = [];
        transformedRecord.accMachineLearningResultList = [];
        transformedRecord.tmrMachineLearningResultList = [];

        if (record.passageWaveforms && Array.isArray(record.passageWaveforms)) {
          record.passageWaveforms.forEach((waveform: any) => {
            const waveformType = waveform.passageWaveformType;
            const waveformId = waveform.passageWaveformId;
            let transformedMLResults: any[] = [];

            if (
              Array.isArray(waveform.machineLearningResults) &&
              waveform.machineLearningResults.length > 0
            ) {
              transformedMLResults = waveform.machineLearningResults.map(
                (mlResult: any) => ({
                  prediction: mlResult.prediction,
                  confidence: mlResult.confidence,
                  weight:
                    mlResult.weight ??
                    mlResult.machineLearningAlgorithm?.weight ??
                    1,
                })
              );
            } else if (
              waveform.mlPrediction !== undefined &&
              waveform.mlConfidence !== undefined
            ) {
              transformedMLResults = [
                {
                  prediction: waveform.mlPrediction,
                  confidence: waveform.mlConfidence,
                  weight: 1,
                },
              ];
            }

            if (waveformType === 'E') {
              transformedRecord.elfId = waveformId;
              transformedRecord.elfIsVerified = waveform.isVerified || false;
              transformedRecord.elfMachineLearningResultList =
                transformedMLResults;
            } else if (waveformType === 'G') {
              transformedRecord.geoId = waveformId;
              transformedRecord.geoIsVerified = waveform.isVerified || false;
              transformedRecord.geoMachineLearningResultList =
                transformedMLResults;
            } else if (waveformType === 'M') {
              transformedRecord.magId = waveformId;
              transformedRecord.magIsVerified = waveform.isVerified || false;
              transformedRecord.magMachineLearningResultList =
                transformedMLResults;
            } else if (waveformType === 'A') {
              transformedRecord.accId = waveformId;
              transformedRecord.accIsVerified = waveform.isVerified || false;
              transformedRecord.accMachineLearningResultList =
                transformedMLResults;
            } else if (waveformType === 'T') {
              transformedRecord.tmrId = waveformId;
              transformedRecord.tmrIsVerified = waveform.isVerified || false;
              transformedRecord.tmrMachineLearningResultList =
                transformedMLResults;
            }
          });

          transformedRecord.isVerified =
            transformedRecord.elfIsVerified ||
            transformedRecord.geoIsVerified ||
            transformedRecord.magIsVerified ||
            transformedRecord.accIsVerified ||
            transformedRecord.tmrIsVerified ||
            false;
        }

        if (record.uid !== undefined) {
          transformedRecord.uid = record.uid;
          transformedRecord.uId = record.uid;
        }

        return transformedRecord;
      });
    },
    updateRowFromPassageVerified(rows: any[], p: any) {
      const { elfId, geoId, magId, accId, tmrId } = p;
      rows.forEach((row: any) => {
        if (!row.passageWaveforms || !Array.isArray(row.passageWaveforms)) {
          return;
        }
        const hasMatchingWaveform = row.passageWaveforms.some(
          (wf: any) =>
            wf.passageWaveformId === elfId ||
            wf.passageWaveformId === geoId ||
            wf.passageWaveformId === magId ||
            wf.passageWaveformId === accId ||
            wf.passageWaveformId === tmrId
        );
        if (!hasMatchingWaveform) {
          return;
        }
        row.elfIsVerified = p.elfIsVerified || false;
        row.geoIsVerified = p.geoIsVerified || false;
        row.magIsVerified = p.magIsVerified || false;
        row.accIsVerified = p.accIsVerified || false;
        row.tmrIsVerified = p.tmrIsVerified || false;
        row.isVerified =
          row.elfIsVerified ||
          row.geoIsVerified ||
          row.magIsVerified ||
          row.accIsVerified ||
          row.tmrIsVerified ||
          false;
      });
    },
    handlePassageVerified({ passage }: IPassageVerified) {
      const p: any = passage;
      this.updateRowFromPassageVerified(this.devicesReportDataArray, p);
      this.updateRowFromPassageVerified(this.completedPigRunReportDataArray, p);
    },
    handlePassageTimestampUpdated() {
      if (
        this.getSelectedReportTypeValue() === 'CompletedPigRun' &&
        this.showCompletedPigRunReportDialog
      ) {
        this.onCompletedPigRunGenerateReportClick();
      } else if (
        this.getSelectedReportTypeValue() === 'Device' &&
        this.showDevicesReportDialog
      ) {
        this.onGenerateReportDataForDevicesReportClick();
      }
    },
    onReportManagerCloseClick() {
      this.$store.dispatch('showPigRunManager');
    },
    onReportTypeSelect() {
      this.showReportTypeDevice =
        this.getSelectedReportTypeValue() === 'Device';
      this.showReportTypeCompletedPigRun =
        this.getSelectedReportTypeValue() === 'CompletedPigRun';
      if (!this.showReportTypeCompletedPigRun) {
        this.$store.commit('SET_SELECTED_COMPLETED_PIG_RUN_ID', null);
      }
    },
    onCompletedPigRunSelect() {
      this.completedPigRunReportDataArray = [];
      this.$store.commit(
        'SET_SELECTED_COMPLETED_PIG_RUN_ID',
        this.getSelectedCompletedPigRunId()
      );
    },
    isOnlySingleTypeOfPassage(props: any): boolean {
      return (
        !!props.row.passageWaveforms &&
        Array.isArray(props.row.passageWaveforms) &&
        props.row.passageWaveforms.length === 1
      );
    },
    getWaveformLabel(waveformType: string): string {
      switch (waveformType) {
        case 'E':
          return 'ELF';
        case 'G':
          return 'GEO';
        case 'M':
          return 'MAG';
        case 'A':
          return 'ACC';
        case 'T':
          return 'TMR';
        default:
          return waveformType;
      }
    },
    showChart(props: any, passageType: string) {
      this.$store.dispatch('setPassageDialogSource', 'report_manager');
      const typeMap: Record<string, number | null | undefined> = {
        ELF: props.row.elfId,
        GEO: props.row.geoId,
        MAG: props.row.magId,
        ACC: props.row.accId,
        TMR: props.row.tmrId,
      };
      const waveformId = typeMap[passageType];
      if (waveformId === null || waveformId === undefined) {
        return;
      }
      this.$store.dispatch('addPassageIdsToUpdate', [waveformId]);
      const passageCopy = { ...props.row, requestedWaveformId: waveformId };
      this.$store.dispatch('passageChartShow', passageCopy);
    },
    handleSwitchPassage({
      passage,
      direction,
      type,
    }: {
      passage: any;
      direction: 'next' | 'previous';
      type: string;
    }) {
      const selectedType = this.getSelectedReportTypeValue();
      const currentDataArray =
        selectedType === 'CompletedPigRun'
          ? this.completedPigRunReportDataArray
          : selectedType === 'Device'
          ? this.devicesReportDataArray
          : [];

      if (!currentDataArray.length) {
        return;
      }

      const currentWaveformId =
        passage.requestedWaveformId || passage.passageId;
      const currentIndex = currentDataArray.findIndex(
        (psg: any) =>
          psg.elfId === currentWaveformId ||
          psg.geoId === currentWaveformId ||
          psg.magId === currentWaveformId ||
          psg.accId === currentWaveformId ||
          psg.tmrId === currentWaveformId
      );
      if (currentIndex === -1) {
        return;
      }
      const inc = direction === 'next' ? 1 : -1;
      const nextIndex =
        (currentIndex + inc + currentDataArray.length) %
        currentDataArray.length;
      this.showChart({ row: currentDataArray[nextIndex] }, type);
    },
    getMLResultColor(data: any): MachineLearningResultColors {
      return DisplayMLResult(data).color;
    },
    getMLResultConfidence(data: any): string {
      return Math.round(DisplayMLResult(data).confidence) + '%';
    },
    getMLIcon(color: MachineLearningResultColors): string {
      if (color.includes('red')) {
        return 'block';
      }
      if (color.includes('green')) {
        return 'check_circle_outline';
      }
      return 'help';
    },
    formatAltitude(altitude: any, prefixComma: boolean): string {
      let altitudeReturnValue = '';
      if (altitude != undefined) {
        const altitudeAsNumber: number = parseInt(altitude, 10);
        if (altitudeAsNumber > -1000) {
          altitudeReturnValue = (altitudeAsNumber / 1000).toFixed(1).toString();
          if (prefixComma) {
            altitudeReturnValue = ', ' + altitudeReturnValue;
          }
        }
      }
      return altitudeReturnValue;
    },
    formatCoordinate(coordinate: any, prefixComma: boolean): string {
      let coordinateReturnValue = '';
      if (coordinate != undefined) {
        const coordinateAsNumber: number = parseFloat(coordinate);
        coordinateReturnValue = coordinateAsNumber.toFixed(6).toString();
        if (prefixComma) {
          coordinateReturnValue = ', ' + coordinateReturnValue;
        }
      }
      return coordinateReturnValue;
    },
    async onCompletedPigRunGenerateReportClick() {
      const selectedPigRunId = this.getSelectedCompletedPigRunId();
      if (selectedPigRunId === null) {
        this.validationDialogMessage = 'A completed pig run must be selected.';
        this.validationDialogTitle = 'Information Message';
        this.showValidationMessageDialog = true;
        return;
      }

      this.completedPigRunReportGenerateReportLoading = true;
      const request = new ReportDataForCompletedPigRunRequest(selectedPigRunId);
      request.page = 1;
      request.rowsPerPage = 15;
      request.sortBy = 'passageTimeStamp';
      request.descending = false;
      request.includeVerifiedPassagesOnly =
        this.selectedPigRunIncludeVerifiedPassagesOnly;

      try {
        const response = await PigRunService.getReportDataForCompletedPigRun(
          request
        );
        this.completedPigRunReportDataArray =
          this.transformReportDataForShowChart(
            response.data.reportDataResponseRecordList
          );
        this.completedPigRunReportDataPagination = {
          descending: request.descending,
          page: request.page,
          rowsNumber: response.data.rowsNumber,
          rowsPerPage: request.rowsPerPage,
          sortBy: request.sortBy,
        };
        this.showCompletedPigRunReportDialog = true;
      } catch (exception: any) {
        this.handleException(exception);
      } finally {
        this.completedPigRunReportGenerateReportLoading = false;
      }
    },
    async onCompletedPigRunReportRequest(props: any) {
      const selectedPigRunId = this.getSelectedCompletedPigRunId();
      if (selectedPigRunId === null) {
        return;
      }
      const request = new ReportDataForCompletedPigRunRequest(selectedPigRunId);
      request.page = props.pagination.page;
      request.rowsPerPage = props.pagination.rowsPerPage;
      request.sortBy = props.pagination.sortBy;
      request.descending = props.pagination.descending;
      request.includeVerifiedPassagesOnly =
        this.selectedPigRunIncludeVerifiedPassagesOnly;
      try {
        const response = await PigRunService.getReportDataForCompletedPigRun(
          request
        );
        this.completedPigRunReportDataArray =
          this.transformReportDataForShowChart(
            response.data.reportDataResponseRecordList
          );
        this.completedPigRunReportDataPagination = {
          descending: request.descending,
          page: request.page,
          rowsNumber: response.data.rowsNumber,
          rowsPerPage: request.rowsPerPage,
          sortBy: request.sortBy,
        };
      } catch (exception: any) {
        this.handleException(exception);
      }
    },
    sanitizeCsvText(value: string): string {
      return value.replaceAll('"', '').replaceAll('\n', '');
    },
    buildCsvReportContent(rows: any[]): string {
      let reportContent =
        'AGM Survey Marker,AGM UID,AGM FW,Survey Marker Latitude,Survey Marker Longitude,AGM Latitude,AGM Longitude,AGM Altitude (m),Passage Type,Time Stamp (UTC),Notes,\n';
      rows.forEach((row) => {
        const markerName =
          row.markerName !== undefined
            ? this.sanitizeCsvText(row.markerName)
            : '';
        const markerNote =
          row.markerNote !== undefined
            ? this.sanitizeCsvText(row.markerNote)
            : '';
        reportContent += markerName + ',';
        reportContent += row.uid !== undefined ? row.uid + ',' : ',';
        reportContent +=
          row.firmwareVersion !== undefined ? row.firmwareVersion + ',' : ',';
        reportContent +=
          row.markerLatitude !== undefined ? row.markerLatitude + ',' : ',';
        reportContent +=
          row.markerLongitude !== undefined ? row.markerLongitude + ',' : ',';
        reportContent += row.latitude !== undefined ? row.latitude + ',' : ',';
        reportContent +=
          row.longitude !== undefined ? row.longitude + ',' : ',';
        reportContent +=
          row.altitude !== undefined
            ? this.formatAltitude(row.altitude, false) + ','
            : ',';
        reportContent +=
          row.passageType !== undefined ? row.passageType + ',' : ',';
        reportContent +=
          row.passageTimeStamp !== undefined
            ? ' ' + row.passageTimeStamp + ','
            : ',';
        reportContent += markerNote + '\n';
      });
      return reportContent;
    },
    triggerCsvDownload(content: string, filename: string) {
      const anchorElement = document.createElement('a');
      const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      anchorElement.href = url;
      anchorElement.setAttribute('download', filename);
      anchorElement.click();
    },
    getDevicesReportDataFilename(): string {
      const now = new Date();
      const y = now.getUTCFullYear().toString();
      const m = String(now.getUTCMonth() + 1).padStart(2, '0');
      const d = String(now.getUTCDate()).padStart(2, '0');
      return `${y}-${m}-${d}-ReportData-ByAgm.csv`;
    },
    getCompletedPigRunReportDataFilename(
      pigRunName: string | undefined
    ): string {
      const now = new Date();
      const y = now.getUTCFullYear().toString();
      const m = String(now.getUTCMonth() + 1).padStart(2, '0');
      const d = String(now.getUTCDate()).padStart(2, '0');
      if (pigRunName) {
        return `${y}-${m}-${d}-ReportData-${this.sanitizeCsvText(
          pigRunName
        )}.csv`;
      }
      return `${y}-${m}-${d}-ReportData-CompletedPigRun.csv`;
    },
    async onDownloadCompletedPigRunReportData() {
      if (this.getSelectedReportTypeValue() !== 'CompletedPigRun') {
        return;
      }
      const selectedPigRunId = this.getSelectedCompletedPigRunId();
      if (selectedPigRunId === null) {
        return;
      }

      const request = new ReportDataForCompletedPigRunRequest(selectedPigRunId);
      request.page = 1;
      request.rowsPerPage = 0;
      request.includeVerifiedPassagesOnly =
        this.selectedPigRunIncludeVerifiedPassagesOnly;

      try {
        const response = await PigRunService.getReportDataForCompletedPigRun(
          request
        );
        const rows = response.data?.reportDataResponseRecordList || [];
        const reportContent = this.buildCsvReportContent(rows);
        const pigRunName = (this.selectedCompletedPigRun as any)?.runData?.name;
        this.triggerCsvDownload(
          reportContent,
          this.getCompletedPigRunReportDataFilename(pigRunName)
        );
      } catch (exception: any) {
        this.handleException(exception);
      }
    },
    onToggleExportOptionsForCompletedPigRunReport() {
      this.exportOptionsVisibleCompletedPigRunReport =
        !this.exportOptionsVisibleCompletedPigRunReport;
    },
    onIncludeVerifiedPassagesOnlyCompletedPigRunReportInput() {
      this.onCompletedPigRunGenerateReportClick();
    },
    updateWaveformVerificationInRow(
      row: any,
      passageWaveformId: number,
      isVerified: boolean
    ) {
      if (row.elfId === passageWaveformId) {
        row.elfIsVerified = isVerified;
      } else if (row.geoId === passageWaveformId) {
        row.geoIsVerified = isVerified;
      } else if (row.magId === passageWaveformId) {
        row.magIsVerified = isVerified;
      } else if (row.accId === passageWaveformId) {
        row.accIsVerified = isVerified;
      } else if (row.tmrId === passageWaveformId) {
        row.tmrIsVerified = isVerified;
      }
      if (Array.isArray(row.passageWaveforms)) {
        const target = row.passageWaveforms.find(
          (wf: any) => wf.passageWaveformId === passageWaveformId
        );
        if (target) {
          target.isVerified = isVerified;
        }
      }
      row.isVerified =
        row.elfIsVerified ||
        row.geoIsVerified ||
        row.magIsVerified ||
        row.accIsVerified ||
        row.tmrIsVerified ||
        false;
    },
    requestCompletedTrackingWorksheetRefresh(pigRunId: number) {
      this.$store.commit(
        'REQUEST_COMPLETED_TRACKING_WORKSHEET_REFRESH',
        pigRunId
      );
    },
    async executeVerifyPassage(
      passageWaveformId: number,
      row: any
    ): Promise<boolean> {
      let currentWfVerified = false;
      if (row.elfId === passageWaveformId) {
        currentWfVerified = row.elfIsVerified || false;
      } else if (row.geoId === passageWaveformId) {
        currentWfVerified = row.geoIsVerified || false;
      } else if (row.magId === passageWaveformId) {
        currentWfVerified = row.magIsVerified || false;
      } else if (row.accId === passageWaveformId) {
        currentWfVerified = row.accIsVerified || false;
      } else if (row.tmrId === passageWaveformId) {
        currentWfVerified = row.tmrIsVerified || false;
      }
      const newWfVerified = !currentWfVerified;
      const request = new PassageVerificationRequest(
        passageWaveformId,
        newWfVerified
      );
      try {
        await PigRunService.setPassageWaveformVerification(request);
      } catch (exception: any) {
        this.handleException(exception);
        return false;
      }
      this.updateWaveformVerificationInRow(
        row,
        passageWaveformId,
        newWfVerified
      );
      (this.$refs.pigRunSummary as any)?.refresh();
      return true;
    },
    async onVerifyPassageForCompletedPigRun(row: any) {
      const selectedPigRunId = this.getSelectedCompletedPigRunId();
      if (selectedPigRunId === null) {
        return;
      }

      const waveforms = getAvailableWaveforms(row);
      if (waveforms.length === 0) {
        if (row.passageTimeStamp !== undefined) {
          const manualPassageTimeStamp = DateCommon.ConvertDateAsStringToDate(
            row.passageTimeStamp.replaceAll('.000000', '')
          );
          try {
            await PigRunService.deleteManualPassage(
              new DeleteManualPassageRequest(
                selectedPigRunId,
                manualPassageTimeStamp
              )
            );
            this.requestCompletedTrackingWorksheetRefresh(selectedPigRunId);
            this.onCompletedPigRunGenerateReportClick();
            (this.$refs.pigRunSummary as any)?.refresh();
          } catch (exception: any) {
            this.handleException(exception);
          }
        }
        return;
      }

      if (row.isVerified) {
        const verifiedWaveforms = getVerifiedWaveforms(row);
        let didUpdateVerification = false;
        for (const wf of verifiedWaveforms) {
          didUpdateVerification =
            (await this.executeVerifyPassage(wf.value, row)) ||
            didUpdateVerification;
        }
        if (didUpdateVerification) {
          this.requestCompletedTrackingWorksheetRefresh(selectedPigRunId);
        }
        return;
      }

      if (waveforms.length === 1) {
        if (await this.executeVerifyPassage(waveforms[0].value, row)) {
          this.requestCompletedTrackingWorksheetRefresh(selectedPigRunId);
        }
        return;
      }

      this.waveformOptionsForVerify = waveforms;
      this.selectedWaveformIdForVerify = null;
      this.pendingVerifyPropsRow = row;
      this.pendingVerifySource = 'completedPigRun';
      this.showWaveformSelectionDialog = true;
    },
    onCancelWaveformSelection() {
      this.showWaveformSelectionDialog = false;
      this.pendingVerifyPropsRow = null;
      this.selectedWaveformIdForVerify = null;
      this.pendingVerifySource = '';
    },
    async onConfirmWaveformSelection() {
      if (
        this.pendingVerifyPropsRow &&
        this.selectedWaveformIdForVerify !== null
      ) {
        const didUpdateVerification = await this.executeVerifyPassage(
          this.selectedWaveformIdForVerify,
          this.pendingVerifyPropsRow
        );
        if (
          didUpdateVerification &&
          this.pendingVerifySource === 'completedPigRun'
        ) {
          const selectedPigRunId = this.getSelectedCompletedPigRunId();
          if (selectedPigRunId !== null) {
            this.requestCompletedTrackingWorksheetRefresh(selectedPigRunId);
          }
        }
      }
      this.onCancelWaveformSelection();
    },
    onEnterReportParametersForDevicesReportClick() {
      this.showEnterParametersForDevicesReportDialog = true;
    },
    validateDevicesParameters(
      dialogTitle = 'Invalid report parameter.'
    ): boolean {
      if (!DateCommon.IsDateAsStringValid(this.startDateDevicesReport)) {
        this.validationDialogTitle = dialogTitle;
        this.validationDialogMessage = 'The start date is not valid.';
        this.showValidationMessageDialog = true;
        return false;
      }
      if (!DateCommon.IsDateAsStringValid(this.endDateDevicesReport)) {
        this.validationDialogTitle = dialogTitle;
        this.validationDialogMessage = 'The end date is not valid.';
        this.showValidationMessageDialog = true;
        return false;
      }
      this.startDateDevicesReportAsDate = DateCommon.ConvertDateAsStringToDate(
        this.startDateDevicesReport
      );
      this.endDateDevicesReportAsDate = DateCommon.ConvertDateAsStringToDate(
        this.endDateDevicesReport
      );
      if (
        this.startDateDevicesReportAsDate.getTime() >=
        this.endDateDevicesReportAsDate.getTime()
      ) {
        this.validationDialogTitle = dialogTitle;
        this.validationDialogMessage =
          'The start date must be before the end date.';
        this.showValidationMessageDialog = true;
        return false;
      }
      if (this.selectedDevicesForDevicesReport.length === 0) {
        this.validationDialogTitle = dialogTitle;
        this.validationDialogMessage =
          'At least one uid value must be selected.';
        this.showValidationMessageDialog = true;
        return false;
      }
      return true;
    },
    onSetParametersForDevicesReportClick() {
      if (this.validateDevicesParameters()) {
        this.showEnterParametersForDevicesReportDialog = false;
      }
    },
    async onGenerateReportDataForDevicesReportClick() {
      if (!this.validateDevicesParameters()) {
        return;
      }
      const request = new ReportDataForDevicesRequest();
      request.page = 1;
      request.rowsPerPage = 15;
      request.sortBy = 'passageTimeStamp';
      request.descending = false;
      request.startDate = this.startDateDevicesReportAsDate;
      request.endDate = this.endDateDevicesReportAsDate;
      request.includeVerifiedPassagesOnly =
        this.includeVerifiedPassagesOnlyDevicesReport;
      request.uidList = [...this.selectedDevicesForDevicesReport];

      this.devicesReportGenerateReportLoading = true;
      try {
        const response = await PigRunService.getReportDataForDevices(request);
        this.devicesReportDataArray = this.transformReportDataForShowChart(
          response.data.reportDataResponseRecordList
        );
        this.devicesReportDataPagination = {
          descending: request.descending,
          page: request.page,
          rowsNumber: response.data.rowsNumber,
          rowsPerPage: request.rowsPerPage,
          sortBy: request.sortBy,
        };
        this.showDevicesReportDialog = true;
      } catch (exception: any) {
        this.handleException(exception);
      } finally {
        this.devicesReportGenerateReportLoading = false;
      }
    },
    async onDevicesReportRequest(props: any) {
      const request = new ReportDataForDevicesRequest();
      request.page = props.pagination.page;
      request.rowsPerPage = props.pagination.rowsPerPage;
      request.sortBy = props.pagination.sortBy;
      request.descending = props.pagination.descending;
      request.startDate = this.startDateDevicesReportAsDate;
      request.endDate = this.endDateDevicesReportAsDate;
      request.includeVerifiedPassagesOnly =
        this.includeVerifiedPassagesOnlyDevicesReport;
      request.uidList = [...this.selectedDevicesForDevicesReport];

      try {
        const response = await PigRunService.getReportDataForDevices(request);
        this.devicesReportDataArray = this.transformReportDataForShowChart(
          response.data.reportDataResponseRecordList
        );
        this.devicesReportDataPagination = {
          descending: request.descending,
          page: request.page,
          rowsNumber: response.data.rowsNumber,
          rowsPerPage: request.rowsPerPage,
          sortBy: request.sortBy,
        };
        this.showDevicesReportDialog = true;
      } catch (exception: any) {
        this.handleException(exception);
      }
    },
    async onDownloadDevicesReportData() {
      if (
        !this.startDateDevicesReportAsDate ||
        !this.endDateDevicesReportAsDate
      ) {
        return;
      }
      const request = new ReportDataForDevicesRequest();
      request.page = 1;
      request.rowsPerPage = 0;
      request.sortBy = 'passageTimeStamp';
      request.descending = false;
      request.startDate = this.startDateDevicesReportAsDate;
      request.endDate = this.endDateDevicesReportAsDate;
      request.includeVerifiedPassagesOnly =
        this.includeVerifiedPassagesOnlyDevicesReport;
      request.uidList = [...this.selectedDevicesForDevicesReport];
      try {
        const response = await PigRunService.getReportDataForDevices(request);
        const rows = response.data?.reportDataResponseRecordList || [];
        this.triggerCsvDownload(
          this.buildCsvReportContent(rows),
          this.getDevicesReportDataFilename()
        );
      } catch (exception: any) {
        this.handleException(exception);
      }
    },
    onToggleExportOptionsForDevicesReport() {
      this.exportOptionsVisibleDevicesReport =
        !this.exportOptionsVisibleDevicesReport;
    },
    onIncludeVerifiedPassagesOnlyDevicesReportInput() {
      this.onGenerateReportDataForDevicesReportClick();
    },
    async onVerifyPassageForDevicesReport(row: any) {
      const waveforms = getAvailableWaveforms(row);
      if (waveforms.length === 0) {
        return;
      }

      if (row.isVerified) {
        const verifiedWaveforms = getVerifiedWaveforms(row);
        for (const wf of verifiedWaveforms) {
          await this.executeVerifyPassage(wf.value, row);
        }
        return;
      }

      if (waveforms.length === 1) {
        await this.executeVerifyPassage(waveforms[0].value, row);
        return;
      }

      this.waveformOptionsForVerify = waveforms;
      this.selectedWaveformIdForVerify = null;
      this.pendingVerifyPropsRow = row;
      this.pendingVerifySource = 'devices';
      this.showWaveformSelectionDialog = true;
    },
    async fetchPigRunsList() {
      if (this.isPigRunsListLoading) return;
      this.isPigRunsListLoading = true;
      try {
        const response = await PigRunService.getPigRuns();
        this.pigRunsList = response.data;
      } catch (error) {
        console.error('Failed to fetch pig runs list:', error);
      } finally {
        this.isPigRunsListLoading = false;
      }
    },
    hideStartDateProxy() {
      hidePopupProxy(this, 'qStartDateProxy');
    },
    hideStartTimeProxy() {
      hidePopupProxy(this, 'qStartTimeProxy');
    },
    hideEndDateProxy() {
      hidePopupProxy(this, 'qEndDateProxy');
    },
    hideEndTimeProxy() {
      hidePopupProxy(this, 'qEndTimeProxy');
    },
  },
});
</script>
